/**
 * Groups — Step 58, rebuilt for scale in Step 163.
 *
 * Kai: *"The UI for groups etc is crap, what if we have 50 groups in a large
 * org?"* The first version was one card per group, each carrying a dropdown of
 * EVERY agent. At fifty groups and a hundred agents that is fifty cards and
 * five thousand option elements — and nothing to search.
 *
 * Now a master/detail: a searchable list of groups on the left (name, member
 * count), one selected group on the right with a searchable token field for
 * its members. The same size at five groups or five hundred.
 *
 * Membership stays auto-commit on purpose: picking a person IS "add them to
 * the group". There is no draft between choosing a name and the membership
 * existing, and a Save button would only add a step to a one-click act. Each
 * add or remove is its own audited call, so the log reads "added Kai" rather
 * than "changed members".
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Trash2, Users } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { TokenSelect } from '../../components/ui/tokenselect';
import { qk } from '../../lib/queryClient';
import { useConfirm } from '../../lib/confirm';
import { useDirty } from '../../lib/dirty';
import { useUrlState } from '../../lib/urlState';
import { cn } from '../../lib/utils';

interface Person {
  id: string;
  name: string | null;
  email: string;
}
interface Group {
  id: string;
  name: string;
  members: Person[];
  /** Step 97 / 145 */
  description: string | null;
  businessFunction: string | null;
  isRestricted: boolean;
  calendarId: string | null;
  autoAssign: 'off' | 'round_robin' | 'least_load';
}
interface Calendar {
  id: string;
  name: string;
}
const BUSINESS_FUNCTIONS: { value: string; label: string }[] = [
  { value: 'it', label: 'IT' },
  { value: 'infosec', label: 'Information security' },
  { value: 'hr', label: 'HR' },
  { value: 'finance', label: 'Accounting & finance' },
  { value: 'facilities', label: 'Facilities / office administration' },
  { value: 'sales_marketing', label: 'Sales & marketing' },
  { value: 'legal', label: 'Legal' },
  { value: 'operations', label: 'Supply chain / operations' },
  { value: 'other', label: 'Other' },
];

/**
 * Step 163 — the groups list, as a SECTION the Agents & groups page hosts.
 * `GroupsPage` below is kept as a thin wrapper so `/admin/groups` still works.
 */
export function GroupsSection() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useUrlState('group', null);
  const [q, setQ] = useUrlState('gq', '');
  const [newName, setNewName] = useState('');
  // The rename is the one piece of free text here, so it is draft-first like
  // every other free-text edit: a controlled draft, the router guard told when
  // it differs, and Save disabled until it does.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.adminGroups,
    queryFn: () =>
      apiFetch<{ entries: Group[]; agents: Person[]; calendars: Calendar[] }>('/api/admin/groups'),
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminGroups });
    void qc.invalidateQueries({ queryKey: qk.adminAgents });
  };

  const act = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method: string; body?: unknown }) =>
      apiSend<{ id?: string }>(path, method, body),
    onSuccess: (res) => {
      setActionError(res.ok ? null : res.error);
      if (res.ok) invalidate();
    },
  });

  // Hooks before any early return — the rules-of-hooks rule, and the reason
  // this derives from `data?.` rather than waiting for the loaded branch.
  const selectedForDirty = data?.entries.find((g) => g.id === selectedId) ?? null;
  const renameDirty =
    renaming !== null &&
    !!selectedForDirty &&
    renameDraft.trim() !== '' &&
    renameDraft.trim() !== selectedForDirty.name;
  const clearDirty = useDirty(renameDirty);

  if (isPending) return <Skeleton className="h-64 w-full" />;
  if (isError)
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <p className="text-sm font-medium">Groups could not be loaded.</p>
        <p className="text-xs text-muted-foreground">{(error as Error).message}</p>
        <Button className="mt-2" size="sm" variant="outline" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );

  const needle = (q ?? '').trim().toLowerCase();
  const groups = data.entries.filter((g) => !needle || g.name.toLowerCase().includes(needle));
  const selected = data.entries.find((g) => g.id === selectedId) ?? null;

  /**
   * Membership as a diff. The token field hands back the whole new list; the
   * API takes one add or remove at a time so each is audited by name. Fire
   * the deltas — normally exactly one.
   */
  const setMembers = (g: Group, next: string[]) => {
    const cur = new Set(g.members.map((m) => m.id));
    const want = new Set(next);
    for (const id of want)
      if (!cur.has(id))
        act.mutate({ path: `/api/admin/groups/${g.id}/members`, method: 'POST', body: { userId: id } });
    for (const id of cur)
      if (!want.has(id)) act.mutate({ path: `/api/admin/groups/${g.id}/members/${id}`, method: 'DELETE' });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[20rem,1fr]">
      {/* ── The list ─────────────────────────────────────────────────── */}
      <div>
        <div className="relative mb-2">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-8 pl-7 text-sm"
            placeholder="Search groups…"
            value={q ?? ''}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="max-h-[60vh] space-y-1 overflow-y-auto pr-1">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setSelectedId(g.id)}
              className={cn(
                'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selectedId === g.id && 'border-brand bg-brand/5',
              )}
            >
              <span className="truncate font-medium">{g.name}</span>
              <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                {g.members.length} {g.members.length === 1 ? 'member' : 'members'}
              </span>
            </button>
          ))}
          {groups.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
              {needle ? 'No groups match.' : 'No groups yet.'}
            </p>
          )}
        </div>
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            act.mutate({ path: '/api/admin/groups', method: 'POST', body: { name: newName.trim() } });
            setNewName('');
          }}
        >
          <Input
            className="h-8 text-sm"
            placeholder="New group…"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button type="submit" size="sm" disabled={!newName.trim() || act.isPending}>
            <Plus /> Add
          </Button>
        </form>
        {actionError && <p className="mt-2 text-xs text-destructive">{actionError}</p>}
      </div>

      {/* ── The selected group ───────────────────────────────────────── */}
      <div>
        {!selected ? (
          <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            Pick a group to manage who is in it.
          </p>
        ) : (
          <div className="rounded-lg border bg-card">
            <div className="flex items-center gap-3 border-b p-4">
              <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
              {renaming === selected.id ? (
                <form
                  className="flex flex-1 gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!renameDirty) return;
                    act.mutate({
                      path: `/api/admin/groups/${selected.id}`,
                      method: 'PATCH',
                      body: { name: renameDraft.trim() },
                    });
                    setRenaming(null);
                  }}
                >
                  <Input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    className="h-8"
                    aria-label="Group name"
                  />
                  <Button type="submit" size="sm" disabled={!renameDirty || act.isPending}>
                    Save
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(null)}>
                    Cancel
                  </Button>
                </form>
              ) : (
                <>
                  <h2 className="flex-1 truncate font-semibold">{selected.name}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRenameDraft(selected.name);
                      setRenaming(selected.id);
                    }}
                  >
                    Rename
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${selected.name}`}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Delete the group “${selected.name}”?`,
                          body: 'Refused if any ticket still points at it. Members keep their other groups.',
                          destructive: true,
                          confirmLabel: 'Delete',
                        })
                      ) {
                        act.mutate({ path: `/api/admin/groups/${selected.id}`, method: 'DELETE' });
                        // Deleting closes the editor, which is a navigation —
                        // and a half-typed rename must not block it with a
                        // dialog offering to keep a group that is now gone.
                        clearDirty();
                        setSelectedId(null);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </>
              )}
            </div>
            {/* Step 97 / 145 — the group's settings, from the reference's Agent
                Group Edit: business function, business hours, auto-assignment,
                restricted. Each is an enumerated property saved on change (the
                approved deviation); the name above stays draft-first. */}
            <div className="grid gap-3 border-b p-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Business function
                </label>
                {/* Auto-commit on purpose: an enumerated group property — the approved deviation; the group's NAME stays draft-first. */}
                <Select
                  value={selected.businessFunction ?? ''}
                  onChange={(e) =>
                    act.mutate({
                      path: `/api/admin/groups/${selected.id}`,
                      method: 'PATCH',
                      body: { businessFunction: e.target.value || null },
                    })
                  }
                  className="h-9 text-sm"
                >
                  <option value="">—</option>
                  {BUSINESS_FUNCTIONS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Business hours</label>
                {/* Auto-commit on purpose: an enumerated group property — the approved deviation; the group's NAME stays draft-first. */}
                <Select
                  value={selected.calendarId ?? ''}
                  onChange={(e) =>
                    act.mutate({
                      path: `/api/admin/groups/${selected.id}`,
                      method: 'PATCH',
                      body: { calendarId: e.target.value || null },
                    })
                  }
                  className="h-9 text-sm"
                >
                  <option value="">The SLA policy's own</option>
                  {data.calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  SLA clocks on this group's tickets run on these hours.
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Auto-assignment
                </label>
                {/* Auto-commit on purpose: an enumerated group property — the approved deviation; the group's NAME stays draft-first. */}
                <Select
                  value={selected.autoAssign}
                  onChange={(e) =>
                    act.mutate({
                      path: `/api/admin/groups/${selected.id}`,
                      method: 'PATCH',
                      body: { autoAssign: e.target.value },
                    })
                  }
                  className="h-9 text-sm"
                >
                  <option value="off">Off — assign by hand</option>
                  <option value="round_robin">Round-robin between available members</option>
                  <option value="least_load">Least-loaded available member</option>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Runs through the "Auto-assign new tickets within their group" rule — activate it under
                  Automation rules.
                </p>
              </div>
              <label className="flex items-start gap-2 pt-5 text-sm">
                {/* Auto-commit on purpose: an enumerated group property — the approved deviation; the group's NAME stays draft-first. */}
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input"
                  checked={selected.isRestricted}
                  onChange={(e) =>
                    act.mutate({
                      path: `/api/admin/groups/${selected.id}`,
                      method: 'PATCH',
                      body: { isRestricted: e.target.checked },
                    })
                  }
                />
                <span>
                  Restricted group
                  <span className="block text-xs text-muted-foreground">
                    Only its members (and admins) can move tickets into it.
                  </span>
                </span>
              </label>
            </div>
            <div className="p-4">
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                Members · {selected.members.length}
              </p>
              <TokenSelect
                value={selected.members.map((m) => m.id)}
                options={data.agents.map((a) => ({ value: a.id, label: a.name ?? a.email }))}
                onChange={(next) => setMembers(selected, next)}
                placeholder="Search agents to add…"
                ariaLabel={`Members of ${selected.name}`}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Changes apply as you make them. A group-scoped role sees this group's tickets.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** `/admin/groups` — kept so old links land somewhere sensible. */
export function GroupsPage() {
  return (
    <div className="p-6">
      <div className="mb-3 flex items-center gap-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Groups</h1>
      </div>
      <GroupsSection />
    </div>
  );
}
